import { useEffect, useRef, useState } from "react"
import { useLanguage } from "../lib/LanguageContext"

const CARD_IMAGES = [
  "https://swimswam.com/wp-content/uploads/2024/07/Pan-Zhanle-By-Jack-Spitser-DSC09819-scaled.jpg",
  "https://cdn.britannica.com/78/255278-050-A5DDD28D/US-Swimmer-Caeleb-Dressel-competes-Medley-relay-final-Tokyo-Olympics-2021.jpg",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTGGc1QhYz_blNRDVfMMrsDP2dTnZCIn1yjx6lJ0qur1cXX9i07yPqRlJ0&s=10",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSc6THZVDebysR1gvfZEVA8LeK8jcmPEhkmF3DZAzLdj0sPi2SIzjkx28pe&s=10",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQMQaQzQTuhCSyakQrzuGDcpvaSODnSUngoAozzMX4fYIVMpkIWi7TuEUY&s=10",
  "https://images.unsplash.com/photo-1560089000-7433a4ebbd64?w=900&auto=format&fit=crop&q=60",
  "https://images.unsplash.com/photo-1600965962361-9035dbfd1c50?w=900&auto=format&fit=crop&q=60",
  "https://images.unsplash.com/photo-1710739513833-48b074290a8e?w=900&auto=format&fit=crop&q=60",
]

const DOT_COUNT = 5
const DOTS = Array.from({ length: DOT_COUNT }, (_, i) => i)

export default function Features() {
  const scrollerRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const activeDot = Math.round((activeIndex / 7) * (DOT_COUNT - 1))
  const { t } = useLanguage()

  const FEATURES = [
    { title: t("feat1_title"), body: t("feat1_body"), core: true },
    { title: t("feat2_title"), body: t("feat2_body"), core: true },
    { title: t("feat3_title"), body: t("feat3_body"), core: true },
    { title: t("feat4_title"), body: t("feat4_body") },
    { title: t("feat5_title"), body: t("feat5_body") },
    { title: t("feat6_title"), body: t("feat6_body") },
    { title: t("feat7_title"), body: t("feat7_body") },
    { title: t("feat8_title"), body: t("feat8_body") },
  ]

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    let raf = null
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = null
        let closest = 0
        let minDist = Infinity
        Array.from(el.children).forEach((card, i) => {
          const dist = Math.abs(card.offsetLeft - el.scrollLeft)
          if (dist < minDist) {
            minDist = dist
            closest = i
          }
        })
        setActiveIndex(closest)
      })
    }
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <section id="features" className="bg-ink-700 py-24">
      <div className="container mx-auto max-w-content px-6 sm:px-16">
        <h2 className="mb-12 text-balance font-display text-4xl font-bold text-paper-0">{t("features_title")}</h2>
        <div
          ref={scrollerRef}
          role="region"
          aria-label={`${t("features_title")}, ${FEATURES.length} cards`}
          className="scrollbar-hide flex cursor-grab gap-6 overflow-x-auto pt-3 [scroll-snap-type:x_mandatory] active:cursor-grabbing"
        >
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              tabIndex={0}
              role="group"
              aria-label={`${i + 1} of ${FEATURES.length}: ${f.title}`}
              className={`carousel-card relative flex h-80 w-[clamp(240px,80vw,320px)] shrink-0 flex-col justify-end overflow-hidden border bg-ink-800 [scroll-snap-align:start] transition-colors hover:-translate-y-0.5 ${
                f.core ? "border-gold-500/40 hover:border-gold-400/70" : "border-white/[0.16] hover:border-white/30"
              }`}
            >
              <img src={CARD_IMAGES[i]} alt="" className="absolute inset-0 h-full w-full object-cover object-top opacity-60" />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(10,29,38,0) 0%, rgba(10,29,38,.55) 55%, rgba(10,29,38,.96) 100%)" }}
              />
              <div className="relative flex flex-col gap-2 p-6">
                <div className={`font-semibold ${f.core ? "text-lg text-gold-200" : "text-paper-0"}`}>{f.title}</div>
                <div className="text-sm leading-[1.7] text-fog-200">{f.body}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-center gap-3" aria-hidden="true">
          {DOTS.map((i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 transition-colors ${i === activeDot ? "bg-gold-400" : "bg-white/30"}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
